import React, { useState } from 'react';
import { 
  Calculator, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Lock, 
  DollarSign, 
  ArrowRight,
  Info
} from 'lucide-react';

export const BreachCalculatorPage: React.FC = () => {
  const [selectedItems, setSelectedItems] = useState<string[]>(['driver_licence', 'email', 'phone']);

  const items = [
    { id: 'driver_licence', label: 'Driver Licence Number & Card #', points: 35, desc: 'Tier-1 DVS Document' },
    { id: 'medicare', label: 'Medicare Card & IRN', points: 25, desc: 'Tier-2 Health & Identity Record' },
    { id: 'passport', label: 'Passport Bio-Page & MRZ', points: 30, desc: 'Tier-1 International Travel ID' },
    { id: 'tfn', label: 'Tax File Number (TFN)', points: 25, desc: 'Permanent ATO Identifier' },
    { id: 'dob', label: 'Full Date of Birth', points: 15, desc: 'Foundational Verification Datum' },
    { id: 'address', label: 'Current Residential Address', points: 10, desc: 'Credit Bureau Address Match' },
    { id: 'email', label: 'Primary Email Address', points: 10, desc: 'Credential Stuffing & Phishing Target' },
    { id: 'phone', label: 'Australian Mobile Phone', points: 15, desc: 'SIM-Swapping Vector' },
    { id: 'bank_bsb', label: 'Bank BSB & Account Number', points: 20, desc: 'Direct Debit & Redirection Risk' }
  ];

  const toggleItem = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const totalPoints = selectedItems.reduce((acc, id) => {
    const item = items.find(x => x.id === id);
    return acc + (item ? item.points : 0);
  }, 0);

  const riskScore = Math.min(100, Math.round((totalPoints / 120) * 100));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Calculator className="w-3.5 h-3.5" />
          <span>STATISTICAL IDENTITY TAKEOVER ALGORITHM</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Personal Data Breach Exposure Risk Calculator</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Select the specific data classes exposed in past corporate breaches to compute your exact probability of financial fraud and identity takeover.
        </p>
      </div>

      {/* Grid of Data Classes */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">Select Exposed Personal Identifiers:</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
          {items.map((item) => {
            const isSelected = selectedItems.includes(item.id);
            return (
              <div
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={`p-4 rounded-2xl border cursor-pointer transition-all space-y-1 ${
                  isSelected 
                    ? 'bg-emerald-950/40 border-emerald-500 text-white shadow-md' 
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between font-bold">
                  <span>{item.label}</span>
                  <input type="checkbox" checked={isSelected} readOnly className="accent-emerald-500" />
                </div>
                <div className="text-[11px] text-slate-400">{item.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calculator Result Box */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl text-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-mono">Calculated Takeover Probability</span>
            <div className="text-2xl font-extrabold text-white">
              {riskScore >= 70 ? 'CRITICAL IDENTITY RISK' : riskScore >= 40 ? 'ELEVATED RISK' : 'MODERATE EXPOSURE'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold text-emerald-400 font-mono">{riskScore}%</div>
            <span className="text-[10px] text-slate-400">Takeover Index</span>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-bold text-white">Critical Australian Vulnerabilities for this Combo:</h4>
          <ul className="space-y-1.5 text-slate-300 list-disc pl-5">
            {selectedItems.includes('driver_licence') && selectedItems.includes('medicare') && (
              <li className="text-rose-300 font-semibold">100-Point ID Verification Complete: Fraudsters can open digital bank accounts and apply for personal loans.</li>
            )}
            {selectedItems.includes('phone') && (
              <li>SIM-Swap Vulnerability: Attackers can attempt carrier porting to intercept SMS NetCodes.</li>
            )}
            {selectedItems.includes('tfn') && (
              <li>ATO Lodgement Risk: Fraudulent tax refund redirects or superannuation early-release attempts.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};
